import { z } from 'zod';
import { CableOrientation } from '../validators/fields/CableOrientation';
import { CableSupportType } from '../validators/fields/CableSupportType';
import { ConnectionCores } from './ConnectionCores';

export enum NECInstallationMethod {
    FREE_AIR = 'Free Air',
    BURIED = 'Buried',
}

export const NECInstallationMethodOptions = [
    {
        value: NECInstallationMethod.FREE_AIR,
        label: 'Free Air',
    },
    {
        value: NECInstallationMethod.BURIED,
        label: 'Buried',
    },
];

export const DefaultNECInstallationMethod = NECInstallationMethod.BURIED;

export enum IECReferenceInstallationMethod {
    A1 = 'A1',
    A2 = 'A2',
    B1 = 'B1',
    B2 = 'B2',
    C = 'C',
    D1 = 'D1',
    D2 = 'D2',
    E = 'E',
    F = 'F',
    G = 'G',
}

export const DefaultIECReferenceInstallationMethod = IECReferenceInstallationMethod.A1;

export const IECReferenceInstallationMethodOptions = [
    {
        value: IECReferenceInstallationMethod.A1,
        label: 'A1 - Insulated conductors (single-core cables) in conduit in a thermally insulated wall',
    },
    {
        value: IECReferenceInstallationMethod.A2,
        label: 'A2 - Multi-core cable in conduit in a thermally insulated wall',
    },
    {
        value: IECReferenceInstallationMethod.B1,
        label: 'B1 - Insulated conductors (single-core cables) in conduit on a wooden wall',
    },
    {
        value: IECReferenceInstallationMethod.B2,
        label: 'B2 - Multi-core cable in conduit on a wooden wall',
    },
    {
        value: IECReferenceInstallationMethod.C,
        label: 'C - Single-core or multi-core cable on a wooden wall',
    },
    {
        value: IECReferenceInstallationMethod.D1,
        label: 'D1 - Multi-core cable in ducts in the ground',
    },
    {
        value: IECReferenceInstallationMethod.D2,
        label: 'D2 - Sheathed single-core or multi-core cables direct in the ground',
    },
    {
        value: IECReferenceInstallationMethod.E,
        label: 'E - Multi-core cable in free air',
    },
    {
        value: IECReferenceInstallationMethod.F,
        label: 'F - Single-core cables, touching in free air',
    },
    {
        value: IECReferenceInstallationMethod.G,
        label: 'G - Single-core cables, spaced in free air',
    },
];

export const isUnderground = (method: IECReferenceInstallationMethod | NECInstallationMethod | string) => {
    return (
        method === IECReferenceInstallationMethod.D1 ||
        method === IECReferenceInstallationMethod.D2 ||
        method === NECInstallationMethod.BURIED
    );
};

export const isSingleCore = (method: IECReferenceInstallationMethod | NECInstallationMethod | string) => {
    return (
        method === IECReferenceInstallationMethod.A1 ||
        method === IECReferenceInstallationMethod.B1 ||
        method === IECReferenceInstallationMethod.F ||
        method === IECReferenceInstallationMethod.G
    );
};

export const isMultiCore = (method: IECReferenceInstallationMethod | NECInstallationMethod | string) => {
    return (
        method === IECReferenceInstallationMethod.E ||
        method === IECReferenceInstallationMethod.D1 ||
        method === IECReferenceInstallationMethod.D2
    );
};

export const IECInstallationMethodValidator = z.enum([
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
    '15',
    '16',
    '20',
    '21',
    '22',
    '23',
    '30',
    '31',
    '32',
    '33',
    '34',
    '35',
    '36',
    '40',
    '41',
    '42',
    '43',
    '44',
    '45',
    '46',
    '47',
    '50',
    '51',
    '52',
    '53',
    '54',
    '55',
    '56',
    '57',
    '58',
    '59',
    '60',
    '70',
    '71',
    '72',
    '73',
]);

export const DefaultIECInstallationMethod = '1';

export type IECInstallationMethod = z.infer<typeof IECInstallationMethodValidator>;

export const IECInstallationMethodOptions: { value: IECInstallationMethod; label: string }[] = [
    {
        value: '1',
        label: '1. Insulated conductors or single-core cables in conduit in a thermally insulated wall',
    },
    {
        value: '2',
        label: '2. Multi-core cables in conduit in a thermally insulated wall',
    },
    {
        value: '3',
        label: '3. Multi-core cable direct in a thermally insulated wall',
    },
    {
        value: '4',
        label: '4. Insulated conductors or single-core cables in conduit on a wooden or masonry wall or spaced less than 0.3 × conduit diameter from it',
    },
    {
        value: '5',
        label: '5. Multi-core cable in conduit on a wooden or masonry wall or spaced less than 0.3 × conduit diameter from it',
    },
    {
        value: '6',
        label: '6. Insulated conductors or single-core cables in cable trunking (includes multi-compartment trunking) on a wooden or masonry wall – run horizontally',
    },
    {
        value: '7',
        label: '7. Insulated conductors or single-core cables in cable trunking (includes multi-compartment trunking) on a wooden or masonry wall – run vertically',
    },
    {
        value: '8',
        label: '8. Multi-core cable in cable trunking (includes multi-compartment trunking) on a wooden or masonry wall – run horizontally',
    },
    {
        value: '9',
        label: '9. Multi-core cable in cable trunking (includes multi-compartment trunking) on a wooden or masonry wall – run vertically',
    },
    {
        value: '10',
        label: '10. Insulated conductors or single-core cable in suspended cable trunking',
    },
    {
        value: '11',
        label: '11. Multi-core cable in suspended cable trunking',
    },
    {
        value: '12',
        label: '12. Insulated conductors or single-core cable run in mouldings',
    },
    {
        value: '15',
        label: '15. Insulated conductors in conduit or single-core or multi-core cable in architrave',
    },
    {
        value: '16',
        label: '16. Insulated conductors in conduit or single-core or multi-core cable in window frames',
    },
    {
        value: '20',
        label: '20. Single-core or multi-core cables: – fixed on, or spaced less than 0.3 × cable diameter from a wooden or masonry wall',
    },
    {
        value: '21',
        label: '21. Single-core or multi-core cables: – fixed directly under a wooden or masonry ceiling',
    },
    {
        value: '22',
        label: '22. Single-core or multi-core cables: – spaced from a ceiling',
    },
    {
        value: '23',
        label: '23. Fixed installation of suspended current-using equipment',
    },
    {
        value: '30',
        label: '30. Single-core or multi-core cables: On unperforated tray run horizontally or vertically',
    },
    {
        value: '31',
        label: '31. Single-core or multi-core cables: On perforated tray run horizontally or vertically',
    },
    {
        value: '32',
        label: '32. Single-core or multi-core cables: On brackets or on a wire mesh tray run horizontally or vertically',
    },
    {
        value: '33',
        label: '33. Single-core or multi-core cables: Spaced more than 0.3 times cable diameter from a wall',
    },
    {
        value: '34',
        label: '34. Single-core or multi-core cables: On ladder',
    },
    {
        value: '35',
        label: '35. Single-core or multi-core cable suspended from or incorporating a support wire or harness',
    },
    {
        value: '36',
        label: '36. Bare or insulated conductors on insulators',
    },
    {
        value: '40',
        label: '40. Single-core or multi-core cable in a building void',
    },
    {
        value: '41',
        label: '41. Insulated conductor in conduit in a building void',
    },
    {
        value: '42',
        label: '42. Single-core or multi-core cable in conduit in a building void',
    },
    {
        value: '43',
        label: '43. Insulated conductors in cable ducting in a building void',
    },
    {
        value: '44',
        label: '44. Single-core or multi-core cable in cable ducting in a building void',
    },
    {
        value: '45',
        label: '45. Insulated conductors in cable ducting in masonry having a thermal resistivity not greater than 2 K·m/W',
    },
    {
        value: '46',
        label: '46. Single-core or multi-core cable in cable ducting in masonry having a thermal resistivity not greater than 2 K·m/W',
    },
    {
        value: '47',
        label: '47. Single-core or multi-core cable: – in a ceiling void – in a raised floor',
    },
    {
        value: '50',
        label: '50. Insulated conductors or single-core cable in flush cable trunking in the floor',
    },
    {
        value: '51',
        label: '51. Multi-core cable in flush cable trunking in the floor',
    },
    {
        value: '52',
        label: '52. Insulated conductors or single-core cables in flush cable trunking',
    },
    {
        value: '53',
        label: '53. Multi-core cable in flush trunking',
    },
    {
        value: '54',
        label: '54. Insulated conductors or single-core cables in conduit in an unventilated cable channel run horizontally or vertically',
    },
    {
        value: '55',
        label: '55. Insulated conductors in conduit in an open or ventilated cable channel in the floor',
    },
    {
        value: '56',
        label: '56. Sheathed single-core or multi-core cable in an open or ventilated cable channel run horizontally or vertically',
    },
    {
        value: '57',
        label: '57. Single-core or multi-core cable direct in masonry having a thermal resistivity not greater than 2 K·m/W Without added mechanical protection',
    },
    {
        value: '58',
        label: '58. Single-core or multi-core cable direct in masonry having a thermal resistivity not greater than 2 K·m/W With added mechanical protection',
    },
    {
        value: '59',
        label: '59. Insulated conductors or single-core cables in conduit in masonry',
    },
    {
        value: '60',
        label: '60. Multi-core cables in conduit in masonry',
    },
    {
        value: '70',
        label: '70. Multi-core cable in conduit or in cable ducting in the ground',
    },
    {
        value: '71',
        label: '71. Single-core cable in conduit or in cable ducting in the ground',
    },
    {
        value: '72',
        label: '72. Sheathed single-core or multi-core cables direct in the ground – without added mechanical protection',
    },
    {
        value: '73',
        label: '73. Sheathed single-core or multi-core cables direct in the ground – with added mechanical protection',
    },
];

export const InstallationMethodOptions = [...NECInstallationMethodOptions, ...IECInstallationMethodOptions];

const installationMethodMap: Record<
    IECInstallationMethod,
    {
        description: string;
        note?: string;
        [ConnectionCores.SINGLE]?: IECReferenceInstallationMethod;
        [ConnectionCores.MULTI]?: IECReferenceInstallationMethod;
        orientation?: CableOrientation;
        cableSupportType?: CableSupportType;
    }
> = {
    '1': {
        description: 'Insulated conductors or single-core cables in conduit in a thermally insulated wall',
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.A1,
    },
    '2': {
        description: 'Multi-core cables in conduit in a thermally insulated wall',
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.A2,
    },
    '3': {
        description: 'Multi-core cable direct in a thermally insulated wall',
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.A2,
    },
    '4': {
        description:
            'Insulated conductors or single-core cables in conduit on a wooden or masonry wall or spaced less than 0.3 × conduit diameter from it',
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B1,
    },
    '5': {
        description:
            'Multi-core cable in conduit on a wooden or masonry wall or spaced less than 0.3 × conduit diameter from it',
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
    },
    '6': {
        description:
            'Insulated conductors or single-core cables in cable trunking (includes multi-compartment trunking) on a wooden or masonry wall – run horizontally',
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B1,
        orientation: 'horizontal',
    },
    '7': {
        description:
            'Insulated conductors or single-core cables in cable trunking (includes multi-compartment trunking) on a wooden or masonry wall – run vertically',
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B1,
        orientation: 'vertical',
    },
    '8': {
        description:
            'Multi-core cable in cable trunking (includes multi-compartment trunking) on a wooden or masonry wall – run horizontally',
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        note: 'Under consideration, Method B2 may be used',
        orientation: 'horizontal',
    },
    '9': {
        description:
            'Multi-core cable in cable trunking (includes multi-compartment trunking) on a wooden or masonry wall – run vertically',
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        note: 'Under consideration, Method B2 may be used',
        orientation: 'vertical',
    },
    '10': {
        description: 'Insulated conductors or single-core cable in suspended cable trunking',
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B1,
    },
    '11': {
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description: 'Multi-core cable in suspended cable trunking',
    },
    '12': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.A1,
        description: 'Insulated conductors or single-core cable run in mouldings',
    },
    '15': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.A1,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.A1,
        description: 'Insulated conductors in conduit or single-core or multi-core cable in architrave',
    },
    '16': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.A1,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.A1,
        description: 'Insulated conductors in conduit or single-core or multi-core cable in window frames',
    },
    '20': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.C,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.C,
        description:
            'Single-core or multi-core cables: – fixed on, or spaced less than 0.3 × cable diameter from a wooden or masonry wall',
    },
    '21': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.C,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.C,
        description: 'Single-core or multi-core cables: – fixed directly under a wooden or masonry ceiling',
        note: 'With item 3 of Table B.52.17',
    },
    '22': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.F,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.E,
        description: 'Single-core or multi-core cables: – spaced from a ceiling',
        note: 'Under consideration, Method E may be used',
    },
    '23': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.C,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.C,
        description: 'Fixed installation of suspended current-using equipment',
        note: 'With item 3 of Table B.52.17',
    },
    '30': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.C,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.C,
        description: 'Single-core or multi-core cables: On unperforated tray run horizontally or vertically',
        note: 'With item 2 of Table B.52.17',
        cableSupportType: 'unperforated-cable-tray',
    },
    '31': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.F,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.E,
        description: 'Single-core or multi-core cables: On perforated tray run horizontally or vertically',
        cableSupportType: 'perforated-cable-tray',
    },
    '32': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.F,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.E,
        description:
            'Single-core or multi-core cables: On brackets or on a wire mesh tray run horizontally or vertically',
    },
    '33': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.F,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.E,
        description: 'Single-core or multi-core cables: Spaced more than 0.3 times cable diameter from a wall',
        note: 'or method G',
    },
    '34': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.F,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.E,
        description: 'Single-core or multi-core cables: On ladder',
        cableSupportType: 'cable-ladder',
    },
    '35': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.F,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.E,
        description: 'Single-core or multi-core cable suspended from or incorporating a support wire or harness',
    },
    '36': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.G,
        description: 'Bare or insulated conductors on insulators',
    },
    '40': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B2,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description: 'Single-core or multi-core cable in a building void',
        note: '1,5 De ≤ V < 5 De B2, 5 De ≤ V < 20 De B1',
    },
    '41': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B2,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description: 'Insulated conductor in conduit in a building void',
        note: '1,5 De ≤ V < 20 De B2, V ≥ 20 De B1',
    },
    '42': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B2,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description: 'Single-core or multi-core cable in conduit in a building void',
        note: 'Under consideration, 1,5 De ≤ V < 20 De B2, V ≥ 20 De B1',
    },
    '43': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B2,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description: 'Insulated conductors in cable ducting in a building void',
        note: '1,5 De ≤ V < 20 De B2, V ≥ 20 De B1',
    },
    '44': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B2,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description: 'Single-core or multi-core cable in cable ducting in a building void',
        note: 'Under consideration, 1,5 De ≤ V < 20 De B2, V ≥ 20 De B1',
    },
    '45': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B2,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description:
            'Insulated conductors in cable ducting in masonry having a thermal resistivity not greater than 2 K·m/W',
        note: '1,5 De ≤ V < 5 De B2, 5 De ≤ V < 50 De B1',
    },
    '46': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B2,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description:
            'Single-core or multi-core cable in cable ducting in masonry having a thermal resistivity not greater than 2 K·m/W',
        note: 'Under consideration, 1,5 De ≤ V < 20 De B2, V ≥ 20 De B1',
    },
    '47': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B2,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description: 'Single-core or multi-core cable: – in a ceiling void – in a raised floor',
        note: '1,5 De ≤ V < 5 De B2, 5 De ≤ V < 50 De B1',
    },
    '50': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B1,
        description: 'Insulated conductors or single-core cable in flush cable trunking in the floor',
    },
    '51': {
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description: 'Multi-core cable in flush cable trunking in the floor',
    },
    '52': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B1,
        description: 'Insulated conductors or single-core cables in flush cable trunking',
    },
    '53': {
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description: 'Multi-core cable in flush trunking',
    },
    '54': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B2,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description:
            'Insulated conductors or single-core cables in conduit in an unventilated cable channel run horizontally or vertically',
        note: '1,5 De ≤ V < 20 De B2, V ≥ 20 De B1',
    },
    '55': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B2,
        description: 'Insulated conductors in conduit in an open or ventilated cable channel in the floor',
    },
    '56': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B1,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B1,
        description:
            'Sheathed single-core or multi-core cable in an open or ventilated cable channel run horizontally or vertically',
    },
    '57': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.C,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.C,
        description:
            'Single-core or multi-core cable direct in masonry having a thermal resistivity not greater than 2 K·m/W Without added mechanical protection',
    },
    '58': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.C,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.C,
        description:
            'Single-core or multi-core cable direct in masonry having a thermal resistivity not greater than 2 K·m/W With added mechanical protection',
    },
    '59': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.B1,
        description: 'Insulated conductors or single-core cables in conduit in masonry',
    },
    '60': {
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.B2,
        description: 'Multi-core cables in conduit in masonry',
    },
    '70': {
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.D1,
        description: 'Multi-core cable in conduit or in cable ducting in the ground',
    },
    '71': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.D1,
        description: 'Single-core cable in conduit or in cable ducting in the ground',
    },
    '72': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.D2,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.D2,
        description:
            'Sheathed single-core or multi-core cables direct in the ground – without added mechanical protection',
    },
    '73': {
        [ConnectionCores.SINGLE]: IECReferenceInstallationMethod.D2,
        [ConnectionCores.MULTI]: IECReferenceInstallationMethod.D2,
        description:
            'Sheathed single-core or multi-core cables direct in the ground – with added mechanical protection',
    },
};

export const getReferenceInstallationMethod = (
    method: IECInstallationMethod,
    cores: ConnectionCores,
): {
    referenceMethod: IECReferenceInstallationMethod;
    cableOrientation?: CableOrientation;
    cableSupportType?: CableSupportType;
    cores?: ConnectionCores;
} => {
    const entry = installationMethodMap[method];

    if (!entry) {
        throw new Error(`Invalid installation method: ${method}`);
    }

    const coresIndex =
        cores === ConnectionCores.MULTI_PE || cores === ConnectionCores.MULTI ? ConnectionCores.MULTI : cores;

    let cores_ = cores;

    let referenceMethod = entry[coresIndex];

    if (!referenceMethod) {
        // Toggle cores
        cores_ =
            cores_ === ConnectionCores.MULTI_PE || cores_ === ConnectionCores.MULTI
                ? ConnectionCores.SINGLE
                : ConnectionCores.MULTI;
        referenceMethod = entry[cores_];
    }

    if (!referenceMethod) {
        throw new Error(`Invalid installation method ${method} for cores ${cores}`);
    }

    return {
        referenceMethod,
        cableOrientation: entry.orientation,
        cableSupportType: entry.cableSupportType,
        cores: cores_,
    };
};

export const getDefaultTemperature = (isUnderground: boolean) => {
    const temperature = isUnderground ? 20 : 30;
    return temperature + 273.15;
};
